import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Label,
} from '@shared/ui/primitives';
import { portalMcpApi, type McpOAuthClientConfig } from '@/services/portalMcpApi';
import { McpAdminPageShell } from './McpAdminShell';

export function McpOAuthClientsPage() {
  const [data, setData] = useState<McpOAuthClientConfig | null>(null);
  const [uris, setUris] = useState<string[]>([]);
  const [newUri, setNewUri] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    portalMcpApi
      .getOAuthClients()
      .then((d) => {
        setData(d);
        setUris(d.redirectUris || []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await portalMcpApi.putOAuthClients({ redirectUris: uris });
      setData(updated);
      setUris(updated.redirectUris);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    if (!confirm('OAuth Client Secret을 rotation 하시겠습니까?')) return;
    setSaving(true);
    try {
      const updated = await portalMcpApi.putOAuthClients({ redirectUris: uris, rotateSecret: true });
      setData(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'rotate failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <McpAdminPageShell title="MCP OAuth Client" description="MCP Gateway OAuth Client ID·Redirect URI">
      {error && <Alert variant="error" title="오류" description={error} />}
      {data && (
        <div className="space-y-6 max-w-2xl">
          <div className="space-y-2">
            <Label>Client ID</Label>
            <Input readOnly value={data.clientId} />
          </div>
          <div className="space-y-2">
            <Label>Client Secret</Label>
            <Input readOnly value={data.clientSecretMasked} />
            <Button variant="outline" size="sm" onClick={rotate} disabled={saving}>
              Rotate Secret
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Redirect URIs</Label>
            <ul className="space-y-2">
              {uris.map((uri) => (
                <li key={uri} className="flex gap-2">
                  <Input readOnly value={uri} className="flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => setUris((u) => u.filter((x) => x !== uri))}>
                    삭제
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Input
                placeholder="https://..."
                value={newUri}
                onChange={(e) => setNewUri(e.target.value)}
              />
              <Button
                variant="outline"
                onClick={() => {
                  if (newUri.trim()) {
                    setUris((u) => [...u, newUri.trim()]);
                    setNewUri('');
                  }
                }}
              >
                추가
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Scopes</Label>
            <p className="text-sm text-muted-foreground">{data.scopes?.join(' · ')}</p>
          </div>
          <Button onClick={save} disabled={saving}>
            저장
          </Button>
        </div>
      )}
    </McpAdminPageShell>
  );
}
